import { MetadataRequest } from "../../shared/models/metadata-request.model";
import { Metadata } from "../../shared/models/metadata.model";

export const walkJson = (jsonObj: any, callback: (parent: object, key: string, value: any) => void) => {
    for (let key in jsonObj) {
        if (jsonObj.hasOwnProperty(key)) {
            let value = jsonObj[key];
            callback(jsonObj, key, value);
            if (typeof value === "object" && value !== null) {
                walkJson(value, callback);
            }
        }
    }
}

export const getUrlQueryInfo = (org: string | null, cfg: string | null, request: MetadataRequest) => {
    // Reduce the state to a specified set of keys
    const queryInfo = {
        ...(request.path && { path: request.path }),
        ...(request.includes && { includes: request.includes }),
        ...(request.customProperties && { customProperties: request.customProperties }),
        ...(request.systemProperties && { systemProperties: request.systemProperties }),
        ...(request.validity && { validity: request.validity }),
        ...(request.searchOptions && { searchOptions: request.searchOptions }),
        ...(request.semantic && { semantic: request.semantic }),
        ...(request.contentType && { contentType: request.contentType }),
        ...(request.organization && { organization: request.organization }),
        ...(request.configuration && { configuration: request.configuration }),
        ...(request.folder && { folder: request.folder }),
        ...(request.batchid && { batchid: request.batchid }),
        ...(request.themeid && { themeid: request.themeid }),
    }
    return "?filters=" + encodeURIComponent(JSON.stringify(queryInfo));
}

export const arrayContainsSubstring = (array: Array<string>, substring: string): boolean => {
    for (let item of array) {
        if (item.indexOf(substring) >= 0) {
            return true;
        }
    }
    return false;
}

export const isMatch = (metadata: Metadata, request: MetadataRequest): boolean => {
    if (request.path && metadata.path !== request.path) {
        return false;
    }
    if (request.text
        && !arrayContainsSubstring(metadata.words.keywords, request.text!)
        && !arrayContainsSubstring(metadata.words.h1, request.text!)
        && !arrayContainsSubstring(metadata.words.h2, request.text!)
        && !arrayContainsSubstring(metadata.words.p, request.text!)) {
        return false;
    }
    if (request.contentType && metadata.contentType !== request.contentType) {
        return false;
    }
    if (request.includes && metadata.includes.indexOf(request.includes) < 0) {
        return false;
    }
    if (request.customProperties) {
        for (let key in request.customProperties) {
            // At least one of the values must match
            if (!metadata.properties[key]
                || metadata.properties[key].filter((value) => request.customProperties![key].includes(value)).length === 0) {
                return false;
            }
        }
    }
    if (request.systemProperties) {
        const untypedmeta = metadata as any;
        for (let key in request.systemProperties) {
            if (untypedmeta[key] && !((request.systemProperties![key] as Array<string>).includes(untypedmeta[key]))) {
                return false;
            }
        }
    }
    // If no validity is set, we don't filter
    if (request.validity && !metadata.validity.includes(request.validity!)) {
        return false;
    }
    return true;
}

export const getResult = async (res: Response, expectArray?: boolean, showSnackbar?: (message: string, severity: string) => void, logout?: () => void) => {
    if (res.status === 200) {
        const contentType = res.headers.get("content-type");
        if (contentType?.includes("application/json")) {
            const json = await res.json();
            const success = json.success;
            if (!success) {
                if (showSnackbar) {
                    showSnackbar(json.message, "error");
                } else {
                    //React Hook not supported, so we can't use the snackbar here
                    console.log(json.message);
                    return expectArray ? [] : null;
                }
            }
            const body = json.body;
            if (expectArray && typeof body === "string") {
                console.error("Expected JSON array API response but received string (likely HTML)");
                return [];
            }
            return body;
        } else {
            const text = await res.text();
            if (expectArray) {
                console.error("Expected JSON array API response but received:", contentType);
                return [];
            }
            return text;
        }
    } else if (res.status === 401 || res.status === 403) {
        if (logout) {
            return logout();
        } else {
            throw new Error(res.statusText);
        }
    }
    console.log("Error: " + res.statusText)
    return expectArray ? [] : null;
}

export const getResultWithError = async (res: Response, showSnackbar?: (message: string, severity: string) => void, logout?: () => void) => {
    const getErrorMessageFromResponse = async (): Promise<string> => {
        const fallbackMessage = res.statusText || "Request failed";
        const contentType = res.headers.get("content-type");
        if (!contentType?.includes("application/json")) {
            return fallbackMessage;
        }
        try {
            const json = await res.json();
            return json?.message || fallbackMessage;
        } catch (_error) {
            return fallbackMessage;
        }
    };

    if (res.status === 200) {
        const contentType = res.headers.get("content-type");
        if (contentType?.includes("application/json")) {
            const json = await res.json();
            const success = json.success;
            if (!success) {
                if (showSnackbar) {
                    showSnackbar(json.message, "error");
                }
                throw new Error(json.message);
            }
            return json.body;
        } else {
            return res.text();
        }
    } else if (res.status === 401 || res.status === 403) {
        if (logout) {
            return logout();
        } else {
            throw new Error(await getErrorMessageFromResponse());
        }
    }
    throw new Error(await getErrorMessageFromResponse());
}

export const getTestResult = (res: any, expectArray?: boolean) => {
    if (res.status === 200) {
        try {
            const json = JSON.parse(res.text);
            const success = json.success;
            if (!success) {
                console.log(json.message);
                return expectArray ? [] : null;
            }
            return json.body;
        } catch (e) {
            return res.text;
        }
    }
    console.log("Error: " + res.statusText)
    return expectArray ? [] : null;
}
